import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import settings from '~/settings';

import queryBattleTypes from '~/queries/battleTypes';
import { useAppDispatch, useAppSelector } from '~/store';
import { settingsActions } from '~/store/slices/settingsSlice';
import { sendErrorNotification } from '~/web2ClientAPI/base';

import type { IBattleTypeData, IBattleTypesResponse } from '~/queries/battleTypes';
import type { RootState } from '~/store';

const stateSelector = (state: RootState) => ({
  battleTypesData: state.settings.battleTypesData,
});

const useVortexBattleTypes = () => {
  const { battleTypesData } = useAppSelector(stateSelector, shallowEqual);
  const dispatch = useAppDispatch();

  const isBattleTypesDataLoaded = Boolean(battleTypesData && Object.keys(battleTypesData).length > 0);

  // Fetch battle types data if not already loaded
  const { loading, data, error } = useQuery<IBattleTypesResponse>(queryBattleTypes, {
    variables: {
      lang: settings.realm?.languageCodeGloss,
    },
    skip: isBattleTypesDataLoaded,
  });

  useEffect(() => {
    if (data) {
      dispatch(settingsActions.setBattleTypes(data.battleTypes));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (error) {
      sendErrorNotification();
    }
  }, [error]);

  const battleTypesMap = useMemo(() => {
    if (!battleTypesData) {
      return;
    }

    const map: Record<string, IBattleTypeData> = {};
    for (const obj of Object.values(battleTypesData)) {
      if (obj) {
        map[obj.id] = obj;
      }
    }
    return map;
  }, [battleTypesData]);

  return {
    loading: loading || Boolean(error),
    loaded: isBattleTypesDataLoaded,
    battleTypesData,
    battleTypesMap,
  };
};

export default useVortexBattleTypes;
